/**
 * Transform utilities for converting between API shapes and frontend models.
 */

import { CoverageTierEnum } from "@platform/openapi-types";

import { Rate, RateSchedule } from "../types";
import { RateRowModel } from "../types/models";

/**
 * Maps a coverage tier code to a human-readable rate type label.
 */
function coverageTierToRateType(tierCode?: CoverageTierEnum): string {
  if (!tierCode) return "Unknown";

  switch (tierCode) {
    case CoverageTierEnum.employee:
      return "Employee Only";
    case CoverageTierEnum.employeeSpouse:
      return "Employee & Spouse";
    case CoverageTierEnum.employeeChildren:
      return "Employee & Children";
    case CoverageTierEnum.employeeFamily:
      return "Full Family";
    case CoverageTierEnum.employeeDependent:
      return "Employee & Child";
    case CoverageTierEnum.employee2Dependents:
      return "Employee & 2 Children";
    case CoverageTierEnum.spouseOnly:
      return "Spouse Only";
    case CoverageTierEnum.spouseDependent:
      return "Dependents Only";
    case CoverageTierEnum.childOnly:
      return "Child Only";
    default:
      return tierCode;
  }
}

/**
 * Converts a backend RateSchedule (with its rates array) into RateRowModel[].
 *
 * @param schedule - The rate schedule from the API
 * @returns Array of rate row models for display in grid/drawer
 */
export function rateScheduleToRows(schedule?: RateSchedule): RateRowModel[] {
  if (!schedule?.rates || schedule.rates.length === 0) {
    return [];
  }

  return schedule.rates.map((rate: Rate) => ({
    id: rate.id,
    rateType: coverageTierToRateType(rate.coverageTierCode),
    ageBandLower: rate.ageBandLower,
    ageBandUpper: rate.ageBandUpper,
    coverageTierCode: rate.coverageTierCode,
    rate: rate.rate,
    volume: rate.volume,
    unit: rate.unit,
    numberOfLives: rate.numberOfLives,
  }));
}

/**
 * Placeholder: Convert RatesTab internal state to RateRowModel[].
 *
 * This will be fleshed out later when we map age bands/tiers/units from the drawer
 * into actual rows. For now, it returns the existing rows unchanged.
 *
 * @param existingRows - Current rate rows
 * @returns Updated rate rows based on drawer state
 */
export function ratesTabStateToRows(
  existingRows: RateRowModel[],
): RateRowModel[] {
  // TODO: Map drawer's age band configuration, tier selections, and units into new rows
  return existingRows;
}

export const parseIntBaseTen = (val: string): number => parseInt(val, 10);
